/**
 * Независимая проверка пакета розыгрыша. Ничего не берёт на веру: всё пересчитывается
 * из исходных данных пакета теми же публичными правилами (формула индекса, расчёт выплат).
 *
 *  1. Подпись сервера (Ed25519) — пакет не изменён после выдачи.
 *  2. Обязательство: SHA-256(соль:параметры) совпадает с хешем, опубликованным до ставок;
 *     состав и базовые цены в параметрах совпадают с раскрытыми.
 *  3. Бары агрегата: каждый бар, показанный игрокам, пересчитывается из минутных баров акций.
 *  4. P₀, P₁, Δ — из пересчитанных баров.
 *  5. Хронология: ходы и изменения прогноза — до фиксации; состав раскрыт не раньше круга 3.
 *  6. Вклады игроков по кругам совпадают с проводками кредитов.
 *  7. Расчёт: банки, комиссия, выплаты и возвраты пересчитываются заново и сверяются.
 *  8. Проводки: выплаты, возвраты и комиссия совпадают с расчётом; банк розыгрыша закрыт в ноль.
 */
import { verifyAsync } from '@noble/ed25519';
import { settle, type SettlementResult } from '@mt/game-core';
import { aggregateAt, INDEX_BASE, lookupFromArray, roundToTick } from '@mt/market-index';
import { canonicalJson, fromBase64, sha256Hex, utf8 } from './canonical.ts';
import { PACKAGE_FORMAT, type Check, type SignedPackage, type VerificationPackage } from './types.ts';

const num = (x: number, digits = 2) => x.toFixed(digits).replace('.', ',');
const cr = (m: number) => `${num(m / 100)} кр.`;
/** Склонение: plural(5, 'бар', 'бара', 'баров'). */
const plural = (n: number, one: string, few: string, many: string) => {
  const m10 = n % 10;
  const m100 = n % 100;
  return m10 === 1 && m100 !== 11 ? one : m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14) ? few : many;
};

export interface VerifyOptions {
  /** ожидаемый открытый ключ сервера (base64); если задан — сверяется с ключом в файле */
  trustedPublicKey?: string;
}

export interface VerifyReport {
  ok: boolean;
  checks: Check[];
  summary: string;
}

export async function verifyPackage(file: unknown, opts: VerifyOptions = {}): Promise<VerifyReport> {
  const checks: Check[] = [];
  const add = (c: Check) => checks.push(c);

  const signed = file as SignedPackage;
  if (!signed || typeof signed !== 'object' || !signed.package || signed.package.format !== PACKAGE_FORMAT) {
    return { ok: false, checks: [{ id: 'format', title: 'Формат файла', status: 'fail', details: ['это не пакет проверки «Рыночного стола»'] }], summary: 'файл не распознан' };
  }
  const pkg = signed.package;

  add(await checkSignature(signed, opts));
  add(await checkCommitment(pkg));
  const bars = checkAggregate(pkg);
  add(bars.check);
  add(checkPrices(pkg, bars.recomputed));
  add(checkTimeline(pkg));
  add(checkContributions(pkg));
  const recomputed = checkSettlement(pkg);
  add(recomputed.check);
  add(checkLedger(pkg));

  const failed = checks.filter((c) => c.status === 'fail');
  const ok = failed.length === 0;
  return {
    ok,
    checks,
    summary: ok
      ? `Все проверки пройдены (${checks.filter((c) => c.status === 'ok').length} из ${checks.length}${checks.some((c) => c.status === 'warn') ? ', есть замечания' : ''}).`
      : `Не пройдено: ${failed.map((c) => c.title).join('; ')}.`,
  };
}

async function checkSignature(signed: SignedPackage, opts: VerifyOptions): Promise<Check> {
  const title = 'Подпись сервера (Ed25519)';
  try {
    const s = signed.signature;
    if (!s || s.algorithm !== 'Ed25519') return { id: 'signature', title, status: 'fail', details: ['подпись отсутствует или неизвестный алгоритм'] };
    if (opts.trustedPublicKey && opts.trustedPublicKey !== s.publicKey) {
      return { id: 'signature', title, status: 'fail', details: ['ключ в файле не совпадает с опубликованным ключом сервера'] };
    }
    const ok = await verifyAsync(fromBase64(s.value), utf8(canonicalJson(signed.package)), fromBase64(s.publicKey));
    return ok
      ? { id: 'signature', title, status: 'ok', details: [`ключ ${s.keyId}${opts.trustedPublicKey ? ' совпадает с опубликованным сервером' : ' (сверьте с /api/verification-key)'}`, 'содержимое пакета не изменялось после выдачи'] }
      : { id: 'signature', title, status: 'fail', details: ['подпись не соответствует содержимому — файл изменён'] };
  } catch (e) {
    return { id: 'signature', title, status: 'fail', details: [`ошибка проверки подписи: ${(e as Error).message}`] };
  }
}

async function checkCommitment(pkg: VerificationPackage): Promise<Check> {
  const title = 'Обязательство: состав зафиксирован до ставок';
  const d: string[] = [];
  let ok = true;
  const c = pkg.commitment;
  const h = await sha256Hex(`${c.salt}:${c.payload}`);
  if (h === c.hash) d.push(`SHA-256(соль:параметры) = ${h.slice(0, 16)}… совпадает с опубликованным хешем`);
  else ((ok = false), d.push(`хеш не совпадает: вычислено ${h}, опубликовано ${c.hash}`));
  let p: Record<string, unknown> = {};
  try {
    p = JSON.parse(c.payload);
  } catch {
    return { id: 'commitment', title, status: 'fail', details: ['параметры обязательства — некорректный JSON'] };
  }
  const symbols = pkg.index.components.map((x) => x.symbol);
  const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
  if (!same(p.symbols, symbols)) ((ok = false), d.push(`состав в обязательстве ${JSON.stringify(p.symbols)} ≠ раскрытому ${JSON.stringify(symbols)}`));
  else d.push(`состав в обязательстве: ${symbols.join(', ')}`);
  if (!same(p.basePrices, pkg.index.components.map((x) => x.basePrice))) ((ok = false), d.push('базовые цены в обязательстве не совпадают с раскрытыми'));
  else d.push('базовые цены совпадают');
  if (p.p0MarketTime !== pkg.index.p0MarketTime) ((ok = false), d.push('время бара P₀ в обязательстве не совпадает'));
  if (p.formula !== pkg.index.formula || p.indexBase !== pkg.index.indexBase || p.tick !== pkg.index.tick) ((ok = false), d.push('формула/база/шаг в обязательстве не совпадают с применёнными'));
  if (p.sessionId !== pkg.index.sessionId) ((ok = false), d.push('идентификатор сессии в обязательстве не совпадает'));
  return { id: 'commitment', title, status: ok ? 'ok' : 'fail', details: d };
}

function checkAggregate(pkg: VerificationPackage) {
  const title = 'Бары агрегата пересчитаны из котировок акций';
  const ix = pkg.index;
  const lookups = ix.components.map((c) => lookupFromArray(c.bars));
  const params = {
    p0MarketTime: Date.parse(ix.p0MarketTime),
    startAt: pkg.timeline.startAt,
    minuteMs: pkg.timeline.minuteMs,
    basePrices: ix.components.map((c) => c.basePrice),
    avgVolumes: ix.components.map((c) => c.avgVolume),
  };
  const recomputed = new Map<number, { o: number; h: number; l: number; c: number; v: number }>();
  const mismatches: string[] = [];
  for (const sb of ix.servedBars) {
    const r = aggregateAt(lookups, params, sb.k);
    recomputed.set(sb.k, r);
    const diff = (['o', 'h', 'l', 'c', 'v'] as const).filter((f) => Math.abs(r[f] - sb[f]) > 1e-9);
    if (diff.length) mismatches.push(`бар k=${sb.k} (${sb.marketTime}): ${diff.map((f) => `${f}: показано ${sb[f]}, пересчитано ${r[f]}`).join('; ')}`);
  }
  const check: Check = mismatches.length
    ? { id: 'aggregate', title, status: 'fail', details: mismatches.slice(0, 10) }
    : {
        id: 'aggregate',
        title,
        status: 'ok',
        details: [`${ix.servedBars.length} ${plural(ix.servedBars.length, 'бар совпал', 'бара совпали', 'баров совпали')} с пересчётом по формуле ${ix.formula}`, `источник котировок: ${pkg.game.sourceDescription}`],
      };
  return { check, recomputed };
}

function checkPrices(pkg: VerificationPackage, re: Map<number, { c: number }>): Check {
  const title = 'P₀, P₁ и изменение Δ';
  const s = pkg.settlement;
  const d: string[] = [];
  let ok = true;
  const p0 = re.get(-1)?.c;
  if (p0 !== INDEX_BASE || s.p0 !== INDEX_BASE) ((ok = false), d.push(`P₀ должен быть ${INDEX_BASE}: пересчитано ${p0}, в расчёте ${s.p0}`));
  else d.push(`P₀ = ${num(p0)} (закрытие бара ${pkg.index.p0MarketTime})`);
  const p1 = re.get(3)?.c ?? null;
  if (pkg.outcome.kind === 'market') {
    if (p1 === null) ((ok = false), d.push('нет целевой свечи для пересчёта P₁'));
    else {
      if (s.p1 !== p1) ((ok = false), d.push(`P₁: в расчёте ${s.p1}, пересчитано ${p1}`));
      const delta = roundToTick(p1 - INDEX_BASE, pkg.index.tick);
      const ticks = Math.round(delta / pkg.index.tick);
      if (pkg.outcome.deltaTicks !== ticks) ((ok = false), d.push(`Δ в шагах: в расчёте ${pkg.outcome.deltaTicks}, пересчитано ${ticks}`));
      d.push(`P₁ = ${num(p1)} (закрытие целевой минуты ${pkg.index.targetMarketTime}), Δ = ${delta > 0 ? '+' : delta < 0 ? '−' : '±'}${num(Math.abs(delta))}`);
    }
  } else d.push(`исход «${pkg.outcome.kind}» — цена P₁ для расчёта не требовалась`);
  return { id: 'prices', title, status: ok ? 'ok' : 'fail', details: d };
}

function checkTimeline(pkg: VerificationPackage): Check {
  const title = 'Хронология: ходы до фиксации, раскрытия по этапам';
  const t = pkg.timeline;
  const d: string[] = [];
  let ok = true;
  const late = pkg.actions.filter((a) => a.kind === 'action' && a.at > t.lockAt + 1_000);
  if (late.length) ((ok = false), d.push(`ходы после фиксации: ${late.map((a) => `${a.name} ${a.action}`).join(', ')}`));
  else {
    const n = pkg.actions.filter((a) => a.kind === 'action').length;
    d.push(`все ходы (${n}) сделаны до фиксации прогнозов в ${new Date(t.lockAt).toISOString()}`);
  }
  const comp = pkg.actions.find((a) => a.kind === 'reveal' && /состав/i.test(a.text));
  if (comp) {
    if (comp.at < t.startAt + 2 * t.minuteMs - 1_000) ((ok = false), d.push('состав раскрыт раньше начала круга 3'));
    else d.push(`состав раскрыт в ${new Date(comp.at).toISOString()} — с началом круга 3`);
  }
  if (pkg.viewer) {
    const after = pkg.viewer.forecastHistory.filter((f) => f.at > t.lockAt + 1_000);
    if (after.length) ((ok = false), d.push(`изменения вашего прогноза после фиксации: ${after.length}`));
    else d.push(`ваш прогноз менялся ${pkg.viewer.forecastHistory.length} ${plural(pkg.viewer.forecastHistory.length, 'раз', 'раза', 'раз')}, все изменения — до фиксации`);
    const me = pkg.participants.find((p) => p.playerId === pkg.viewer!.playerId);
    const last = pkg.viewer.forecastHistory[pkg.viewer.forecastHistory.length - 1];
    if (me && last && (last.direction !== me.direction || last.points !== me.points)) ((ok = false), d.push('итоговый прогноз в расчёте отличается от последнего вашего изменения'));
  }
  const unordered = pkg.actions.some((a, i) => i > 0 && a.seq <= pkg.actions[i - 1]!.seq);
  if (unordered) ((ok = false), d.push('журнал ходов не упорядочен'));
  return { id: 'timeline', title, status: ok ? 'ok' : 'fail', details: d };
}

function checkContributions(pkg: VerificationPackage): Check {
  const title = 'Вклады игроков совпадают с проводками';
  const esc = `escrow:${pkg.game.gameId}`;
  const d: string[] = [];
  let ok = true;
  for (const p of pkg.participants) {
    const acc = `player:${p.playerId}`;
    const byRound = [0, 0, 0];
    let ante = 0;
    for (const t of pkg.ledger) {
      const r = Math.max(1, t.round ?? 1) - 1;
      if (t.from === acc && t.to === esc) {
        if (t.kind === 'ante') ante += t.amount;
        else byRound[r] = (byRound[r] ?? 0) + t.amount;
      }
      if (t.from === esc && t.to === acc && t.kind === 'uncalled-return') byRound[r] = (byRound[r] ?? 0) - t.amount;
    }
    const same = ante === p.ante && byRound.every((v, i) => v === (p.roundBets[i] ?? 0));
    if (!same) ((ok = false), d.push(`${p.name}: по проводкам взнос ${cr(ante)}, круги ${byRound.map(cr).join(' / ')}; в расчёте взнос ${cr(p.ante)}, круги ${p.roundBets.map(cr).join(' / ')}`));
  }
  if (ok) d.push(`у ${pkg.participants.length} ${plural(pkg.participants.length, 'участника', 'участников', 'участников')} взносы и ставки по кругам совпадают с проводками`);
  return { id: 'contributions', title, status: ok ? 'ok' : 'fail', details: d };
}

function checkSettlement(pkg: VerificationPackage): { check: Check; result: SettlementResult | null } {
  const title = 'Расчёт выплат пересчитан заново';
  const seats = [...pkg.participants]
    .sort((a, b) => a.order - b.order)
    .map((p) => ({ playerId: p.playerId, order: p.order, ante: p.ante, roundBets: p.roundBets, folded: p.folded, direction: p.direction, pointsTicks: p.pointsTicks }));
  let r: SettlementResult;
  try {
    r = settle(seats, pkg.outcome, pkg.rules);
  } catch (e) {
    return { check: { id: 'settlement', title, status: 'fail', details: [`не удалось пересчитать: ${(e as Error).message}`] }, result: null };
  }
  const s = pkg.settlement;
  const d: string[] = [];
  let ok = true;
  if (r.status !== s.status) ((ok = false), d.push(`статус: в пакете «${s.status}», пересчитано «${r.status}»`));
  for (const rp of r.players) {
    const sp = s.players.find((x) => x.playerId === rp.playerId);
    if (!sp) {
      ok = false;
      d.push(`нет игрока ${rp.playerId} в расчёте`);
      continue;
    }
    if (sp.payout !== rp.payout || sp.refund !== rp.refund || sp.contributed !== rp.contributed) {
      ok = false;
      d.push(`${sp.name}: выплата ${cr(sp.payout)} / возврат ${cr(sp.refund)} в пакете, пересчитано ${cr(rp.payout)} / ${cr(rp.refund)}`);
    }
  }
  const commIn = s.commissionByRound.map((x) => x.commission).join(',');
  const commRe = r.commissionByRound.map((x) => x.commission).join(',');
  if (commIn !== commRe) ((ok = false), d.push(`комиссия по кругам: в пакете ${commIn}, пересчитано ${commRe}`));
  if (s.bankDelta !== r.bankWithheld) ((ok = false), d.push(`удержано банком: в пакете ${cr(s.bankDelta)}, пересчитано ${cr(r.bankWithheld)}`));
  if (!r.totals.balanced) ((ok = false), d.push('пересчитанный расчёт не сходится'));
  if (ok) {
    d.push(`исход: ${r.kind === 'market' ? 'по рынку' : r.kind === 'walkover' ? 'остался один участник' : 'отмена'}${r.reason ? ` (${r.reason})` : ''}`);
    d.push(`банков: ${r.pots.length}; комиссия по кругам ${r.commissionByRound.map((x) => cr(x.commission)).join(' / ')}; удержано ${cr(r.bankWithheld)}`);
    for (const p of r.players.filter((x) => x.payout > 0)) {
      d.push(`${s.players.find((x) => x.playerId === p.playerId)?.name}: выплата ${cr(p.payout)}${p.errorTicks !== null ? `, ошибка прогноза ${num(p.errorTicks * pkg.rules.tick)} п.` : ''}`);
    }
    if (r.kind === 'market' && r.players.some((p) => p.payout > 0 && !p.correctDirection)) ((ok = false), d.push('выплата игроку с неверным направлением'));
  }
  return { check: { id: 'settlement', title, status: ok ? 'ok' : 'fail', details: d }, result: r };
}

function checkLedger(pkg: VerificationPackage): Check {
  const title = 'Проводки кредитов сходятся';
  const esc = `escrow:${pkg.game.gameId}`;
  const s = pkg.settlement;
  const d: string[] = [];
  let ok = true;
  let net = 0;
  for (const t of pkg.ledger) {
    if (t.to === esc) net += t.amount;
    if (t.from === esc) net -= t.amount;
  }
  if (net !== 0) ((ok = false), d.push(`остаток на счёте розыгрыша: ${cr(net)} (должен быть 0)`));
  else d.push('счёт розыгрыша закрыт в ноль');
  const sum = (kind: string, to?: string) => pkg.ledger.filter((t) => t.from === esc && t.kind === kind && (!to || t.to === to)).reduce((a, t) => a + t.amount, 0);
  for (const p of s.players) {
    const acc = `player:${p.playerId}`;
    if (sum('payout', acc) !== p.payout || sum('refund', acc) !== p.refund) {
      ok = false;
      d.push(`${p.name}: проводки выплат ${cr(sum('payout', acc))} / возвратов ${cr(sum('refund', acc))}, в расчёте ${cr(p.payout)} / ${cr(p.refund)}`);
    }
  }
  const commission = sum('commission', 'house:bank');
  if (commission !== s.bankDelta) ((ok = false), d.push(`комиссия в проводках ${cr(commission)}, в расчёте ${cr(s.bankDelta)}`));
  if (ok) d.push(`списано ${cr(s.totals.debited)} = выплачено ${cr(s.totals.paid)} + возвращено ${cr(s.totals.refunded)} + банк ${cr(s.totals.bank)}`);
  return { id: 'ledger', title, status: ok ? 'ok' : 'fail', details: d };
}
